import { useMemo, useCallback, useEffect, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import useUserMultiCartHotelItemData from '../../hooks/useUserMultiCartHotelItemData'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import { MultiCartReviewMachineContext } from '../../MultiCartReviewMachineContext'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'

interface Props {
  multiCartItems: Array<App.MultiCartFlightItem | App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem>
  tileVariant?: 'default' | 'condensed'
  hideTitleAndDates?: boolean
  hideButtons?: boolean
}

function LeHotelTile({
  multiCartItems,
  tileVariant = 'default',
  hideTitleAndDates = false,
  hideButtons = false,
}: Props) {
  const intl = useIntl()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()

  // Separate flight and hotel items
  const hotelItem = useMemo(() => {
    return multiCartItems.find(item => !isFlightItem(item.item)) as App.MultiCartInstantBookingLEHotelItem | undefined
  }, [multiCartItems])

  const flightItems = useMemo(() => {
    return multiCartItems.filter(item => isFlightItem(item.item)) as Array<App.MultiCartFlightItem>
  }, [multiCartItems])

  const item = hotelItem?.item as App.Checkout.InstantBookingLEHotelItem
  const data = useUserMultiCartHotelItemData(item)

  const formattedDateRange = useMemo(() => {
    if (!hotelItem) return false
    const dateRange = formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut)
    if (!dateRange) return false
    return intl.formatMessage(
      {
        id: 'VEyYa4DloTDcmeqy-T5J',
        defaultMessage: '{dateRange} · {duration, plural, one {# night} other {# nights}}',
        description: 'Date range and stay duration for a LE hotel tile in the flight and hotel bundle cart review',
      },
      {
        dateRange,
        duration: hotelItem.duration,
      },
    )
  }, [intl, hotelItem])

  const formattedOccupancy = useMemo(() => {
    if (!hotelItem?.occupancy) return false
    return formatOccupantsShort(hotelItem.occupancy)
  }, [hotelItem?.occupancy])

  const { publicTotal: hotelPublicTotal, memberTotal: hotelMemberTotal } = useMemo(
    () => getUserCartItemDisplayTotals(data.itemTotals),
    [data?.itemTotals],
  )

  // Calculate flight prices
  const flightTotal = useMemo(() => {
    return flightItems.reduce((total, flightItem) => {
      const flightCheckoutItem = flightItem.item as App.Checkout.FlightItem
      return total + getFlightsTotal(flightCheckoutItem)
    }, 0)
  }, [flightItems])
  const numberOfRoomsAssociatedWithFlight = flightItems[0]?.bundledItemIds?.length ?? 1
  const flightTotalPerRoom = flightTotal / numberOfRoomsAssociatedWithFlight
  // Calculate bundled prices (hotel + flight)
  const bundledPublicTotal = hotelPublicTotal + flightTotalPerRoom
  const bundledMemberTotal = hotelMemberTotal === 0 ? 0 : hotelMemberTotal + flightTotalPerRoom

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, data.isUnavailable, !!data.error)
  const roomsLeft = data.isSoldOut ? 0 : (data.package?.availableRooms ?? 0)
  const image = data.package?.roomType.images[0] || data.offer?.image

  const isValueBeyondNonMemberPrice = (data.itemTotals?.value ?? 0) > hotelPublicTotal
  const shouldDisplayValueUpTo = isValueBeyondNonMemberPrice && (data.package?.shouldDisplayValue === true)

  // Check if all items are saved for later
  const areAllItemsSavedForLater = useMemo(
    () => multiCartItems.every(item => item.state === 'save_for_later'),
    [multiCartItems],
  )

  const isBookable = useMemo(
    () => !multiCartItems.some(item => item.state === 'deleted') && data.isBookable,
    [multiCartItems, data.isBookable],
  )

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => multiCartItems.map(item => item.id), [multiCartItems])

  // Get current states for all items
  const currentStates = useMemo(
    () => multiCartItems.reduce((acc, item) => {
      acc[item.id] = item.state
      return acc
    }, {} as Record<string, App.MultiCartItemState>),
    [multiCartItems],
  )

  useEffect(() => {
    // Only save if explicitly enabled AND item is not bookable AND not already saved/deleted
    if (
      !data.isBookable &&
      hotelItem?.state === 'active' && // Only process active items
      !data.loading &&
      activeCartId
    ) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId,
        itemIds: [hotelItem.id, ...flightItems.map(item => item.id)],
        saveForLater: true,
        skipNotification: true,
      })
    }

    // if flight items are saved for later then save the hotel item as well
    if (flightItems.some(item => item.state === 'save_for_later') && hotelItem?.state === 'active' && activeCartId) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId,
        itemIds: [hotelItem.id],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [data.isBookable, hotelItem?.state, data.loading, activeCartId, hotelItem?.id, flightItems, multiCartReviewActorRef])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: allItemIds,
      saveForLater: !areAllItemsSavedForLater,
    })
  }, [activeCartId, allItemIds, areAllItemsSavedForLater, multiCartReviewActorRef])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: allItemIds,
      currentStates,
    })
  }, [activeCartId, allItemIds, currentStates, multiCartActorRef])

  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.offer || !data.itemTotals) {
      return {
        calculationRequests: [],
        calculationType: 'exact',
      }
    }
    const calculationRequest = generateLoyaltyPointsCalculatorHotelOptions(data.offer, {
      pkg: data.package,
      price: bundledPublicTotal,
      memberPrice: bundledMemberTotal,
      currentRegionCode,
      nights: data.package?.duration,
    })
    return {
      calculationRequests: [calculationRequest],
      calculationType: 'exact',
    }
  }, [data.offer, data.itemTotals, bundledPublicTotal, bundledMemberTotal, currentRegionCode, data.package])

  if (data.loading || !hotelItem) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
        content={<CondensedCartReviewTileContent
          title={data.offer?.property.name || data.offer?.name || ''}
          dates={formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
          roomName={data.package?.roomType.name}
        />}
        price={<CartReviewPriceStack
          price={bundledPublicTotal}
          memberPrice={bundledMemberTotal}
          value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
          luxPlusOfferConfig={data.offer?.luxPlus}
        />}
        banner={<CartReviewBanner variant={bannerTypeVariant} />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        onDelete={hideButtons ? undefined : handleDelete}
      />
    )
  }

  const actions = hideButtons ? undefined : (
    <CartReviewTileActions
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />
  )
  return (
    <CartReviewTile
      label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        roomName={data.package?.roomType.name}
      />}
      price={<CartReviewPriceStack
        price={bundledPublicTotal}
        memberPrice={bundledMemberTotal}
        value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />}
      banner={<CartReviewBanner variant={bannerTypeVariant} />}
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      actions={actions}
      hideAvailabilityDetails={false}
      disableOverlay={false}
    />
  )
}

export default LeHotelTile
